import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Check, X, UserPlus, KeyRound, Copy } from "lucide-react";

export default function ApprovalsDialog({ open, onOpenChange, onChange }) {
  const [users, setUsers] = useState([]);
  const [resets, setResets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [issued, setIssued] = useState(null); // { user_email, temp_password }

  const load = async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/password-resets"),
      ]);
      setUsers(u.data);
      setResets(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setIssued(null);
      load();
    }
  }, [open]);

  const approve = async (uid) => {
    try {
      await api.post(`/admin/users/${uid}/approve`);
      toast.success("User approved");
      load();
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const reject = async (u) => {
    if (!window.confirm(`Remove ${u.email}? This deletes their account.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User removed");
      load();
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const approveReset = async (req) => {
    if (!window.confirm(`Issue a new temporary password for ${req.email}?`)) return;
    try {
      const { data } = await api.post(`/admin/password-resets/${req.id}/approve`);
      setIssued(data);
      toast.success("Temporary password issued");
      load();
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const rejectReset = async (req) => {
    if (!window.confirm(`Reject ${req.email}'s reset request?`)) return;
    try {
      await api.delete(`/admin/password-resets/${req.id}`);
      toast.success("Request rejected");
      load();
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  const pending = users.filter((u) => !u.is_approved);
  const approved = users.filter((u) => u.is_approved);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-hidden flex flex-col" data-testid="approvals-dialog">
        <DialogHeader>
          <DialogTitle className="font-medium tracking-tight">User access</DialogTitle>
          <DialogDescription className="text-stone-500">
            Approve sign-ups, handle password resets, or remove existing viewers.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto -mx-6 px-6 space-y-6 flex-1">
          {/* Issued temp password (one-time view) */}
          {issued && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 space-y-2" data-testid="issued-password">
              <p className="text-sm font-medium text-emerald-900">
                Temporary password issued for {issued.user_email}
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 rounded bg-white border border-emerald-200 text-stone-900 font-mono text-sm select-all">
                  {issued.temp_password}
                </code>
                <Button size="sm" variant="outline" onClick={() => copy(issued.temp_password)} data-testid="copy-temp-password">
                  <Copy className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
                  Copy
                </Button>
              </div>
              <p className="text-xs text-stone-600">
                Share this password with the user securely (e.g. WhatsApp, SMS). It will not be shown again.
              </p>
              <Button size="sm" variant="ghost" onClick={() => setIssued(null)} className="text-stone-600">Dismiss</Button>
            </div>
          )}

          {/* Password reset requests */}
          <section>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-3">
              Password reset requests ({resets.length})
            </p>
            {loading ? (
              <div className="h-16 rounded-md bg-stone-100 animate-pulse" />
            ) : resets.length === 0 ? (
              <p className="text-sm text-stone-500 italic">No pending password resets.</p>
            ) : (
              <ul className="space-y-2">
                {resets.map((r) => (
                  <li key={r.id} className="flex items-center gap-3 rounded-md border border-orange-200 bg-orange-50/60 p-3" data-testid={`reset-${r.id}`}>
                    <KeyRound className="h-4 w-4 text-orange-700 shrink-0" strokeWidth={1.5} />
                    <div className="min-w-0 flex-1">
                      <p className="text-stone-900 font-medium truncate">{r.name || r.email}</p>
                      <p className="text-xs text-stone-500 truncate">{r.email}</p>
                    </div>
                    <Button size="sm" onClick={() => approveReset(r)} className="bg-emerald-900 hover:bg-emerald-800 text-stone-50" data-testid={`approve-reset-${r.id}`}>
                      <Check className="h-4 w-4 mr-1" strokeWidth={2} />
                      Issue
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => rejectReset(r)} className="text-stone-600 hover:text-red-700" data-testid={`reject-reset-${r.id}`}>
                      <X className="h-4 w-4" strokeWidth={2} />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Pending sign-ups */}
          <section>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-3">
              Pending sign-ups ({pending.length})
            </p>
            {loading ? (
              <div className="h-16 rounded-md bg-stone-100 animate-pulse" />
            ) : pending.length === 0 ? (
              <p className="text-sm text-stone-500 italic">No pending requests.</p>
            ) : (
              <ul className="space-y-2">
                {pending.map((u) => (
                  <li key={u.id} className="flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 p-3" data-testid={`pending-${u.id}`}>
                    <UserPlus className="h-4 w-4 text-amber-700 shrink-0" strokeWidth={1.5} />
                    <div className="min-w-0 flex-1">
                      <p className="text-stone-900 font-medium truncate">{u.name || u.email}</p>
                      <p className="text-xs text-stone-500 truncate">{u.email}</p>
                    </div>
                    <Button size="sm" onClick={() => approve(u.id)} className="bg-emerald-900 hover:bg-emerald-800 text-stone-50" data-testid={`approve-${u.id}`}>
                      <Check className="h-4 w-4 mr-1" strokeWidth={2} />
                      Approve
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => reject(u)} className="text-stone-600 hover:text-red-700" data-testid={`reject-${u.id}`}>
                      <X className="h-4 w-4" strokeWidth={2} />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Approved users */}
          <section>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-3">
              Approved ({approved.length})
            </p>
            {approved.length === 0 ? (
              <p className="text-sm text-stone-500 italic">No approved users yet.</p>
            ) : (
              <ul className="space-y-2">
                {approved.map((u) => (
                  <li key={u.id} className="flex items-center gap-3 rounded-md border border-stone-200 bg-white p-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-stone-900 font-medium truncate">{u.name || u.email}</p>
                      <p className="text-xs text-stone-500 truncate">{u.email}</p>
                    </div>
                    <Badge variant="outline" className="border-emerald-200 text-emerald-800 rounded-full">
                      Active
                    </Badge>
                    <Button size="sm" variant="ghost" onClick={() => reject(u)} className="text-stone-500 hover:text-red-700" data-testid={`remove-${u.id}`}>
                      <X className="h-4 w-4" strokeWidth={2} />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
